'use client';

import React from 'react';
import { BirthDetailsForm } from '@/components/birth-form/BirthDetailsForm';
import dynamic from 'next/dynamic';
import type { HouseTableRow } from '@/components/calculators/CalculatorPageContent';

const CalculatorPageContent = dynamic(
  () => import('@/components/calculators/CalculatorPageContent').then((mod) => mod.CalculatorPageContent),
  { ssr: true }
);
import { Compass, ShieldCheck, Orbit, Sparkles, BookOpen, Clock, Star } from 'lucide-react';

const BIRTH_CHART_HOUSE_TABLE: HouseTableRow[] = [
  { houseNumber: 1, sanskritName: 'Tanu Bhava', englishMeaning: 'Self & Vitality', significations: 'Physical appearance, health, personality, general life direction, and Lagna identity.' },
  { houseNumber: 2, sanskritName: 'Dhana Bhava', englishMeaning: 'Wealth & Speech', significations: 'Accumulated wealth, family lineage, speech, eating habits, and early childhood.' },
  { houseNumber: 3, sanskritName: 'Sahaja Bhava', englishMeaning: 'Courage & Siblings', significations: 'Younger siblings, short travels, manual skills, courage, communication, and initiatives.' },
  { houseNumber: 4, sanskritName: 'Sukha Bhava', englishMeaning: 'Home & Comfort', significations: 'Mother, home environment, real estate, vehicles, emotional peace, and foundational security.' },
  { houseNumber: 5, sanskritName: 'Putra Bhava', englishMeaning: 'Creativity & Children', significations: 'Children, intellect, past-life merit (Purva Punya), creative expression, and romance.' },
  { houseNumber: 6, sanskritName: 'Ari Bhava', englishMeaning: 'Health & Obstacles', significations: 'Daily work routine, health challenges, litigation, debts, open enemies, and service.' },
  { houseNumber: 7, sanskritName: 'Yuvati Bhava', englishMeaning: 'Marriage & Partnerships', significations: 'Spouse, long-term business partnerships, public relations, and legal contracts.' },
  { houseNumber: 8, sanskritName: 'Randhra Bhava', englishMeaning: 'Transformation & Longevity', significations: 'Unearned wealth, joint assets, occult research, major life transitions, and longevity.' },
  { houseNumber: 9, sanskritName: 'Dharma Bhava', englishMeaning: 'Fortune & Higher Knowledge', significations: 'Father, spiritual guru, higher education, long-distance journeys, and destiny/dharma.' },
  { houseNumber: 10, sanskritName: 'Karma Bhava', englishMeaning: 'Career & Public Reputation', significations: 'Profession, public achievements, authority, government interaction, and social status.' },
  { houseNumber: 11, sanskritName: 'Labha Bhava', englishMeaning: 'Gains & Aspirations', significations: 'Financial gains, elder siblings, social networks, wish fulfillment, and income.' },
  { houseNumber: 12, sanskritName: 'Vyaya Bhava', englishMeaning: 'Loss & Liberation', significations: 'Expenditures, foreign travel/relocation, subconscious mind, isolation, and Moksha.' }
];

export default function BirthChartCalculatorPage() {
  const introParagraph = "A Vedic Birth Chart (Kundli or Janam Patri) is a 360° celestial map showing the exact planetary longitudes, sign placements, and house boundaries at the precise moment and geographic location of your birth.";

  const methodologyContent = (
    <div className="space-y-3">
      <p>
        When I compute your birth chart on AstroPredict, I use the C-native Swiss Ephemeris library (pyswisseph)—the exact same astronomical engine trusted by professional Vedic software worldwide.
      </p>
      <p>
        I calculate true Lagna (Ascendant) and Graha positions using Lahiri Ayanamsha (sidereal zodiac) and Whole Sign house divisions. This guarantees that planetary longitudes, divisional charts (D1 & D9 Navamsha), Ashtakavarga bindu scores, and Vimshottari Dasha cycles match astronomical reality to exact arc-second precision.
      </p>
    </div>
  );

  const subsections = [
    {
      title: "What Makes Up Your Birth Chart",
      icon: <Sparkles className="w-5 h-5" />,
      content: "Your Kundli synthesizes three fundamental pillars: the 12 Bhavas (houses governing life domains), the 9 Grahas (planets providing cosmic energies), and the 27 Nakshatras (lunar mansions providing psychological themes). Together, they form your unique natal footprint."
    },
    {
      title: "Why Exact Birth Time Matters",
      icon: <Clock className="w-5 h-5" />,
      content: "The Earth rotates 360° every 24 hours, meaning the Ascendant (Lagna) sign shifts approximately every 2 hours, and house cusp boundaries shift by degrees every minute. An accurate birth time to the minute ensures your Lagna lord and house placements reflect your true life structure."
    },
    {
      title: "Vedic vs Western Birth Charts",
      icon: <Star className="w-5 h-5" />,
      content: "While Western astrology uses the Tropical zodiac based on fixed seasonal equinoxes, Vedic astrology uses the Sidereal zodiac aligned with physical constellation coordinates (~24° Lahiri offset). Vedic charts also focus on the Ascendant and Moon sign rather than solely the Sun sign."
    }
  ];

  const faqs = [
    {
      q: "What is a Vedic Birth Chart (Kundli)?",
      a: "A Vedic Birth Chart (Kundli) is an astronomical snapshot of the sky taken at the exact time and location of your birth. It maps the 9 Grahas across 12 houses to outline your life blueprint."
    },
    {
      q: "Do I need my exact birth time to calculate a Kundli?",
      a: "Yes. An exact birth time ensures your Ascendant (Lagna) sign and house boundaries are calculated accurately, which is essential for precise house placement and Dasha timing."
    },
    {
      q: "What is the difference between Vedic (Sidereal) and Western (Tropical) charts?",
      a: "Vedic astrology uses the Sidereal zodiac aligned with physical constellations (~24° Lahiri offset), while Western astrology uses the Tropical zodiac based on seasonal equinoxes."
    },
    {
      q: "What information can I get from my birth chart?",
      a: "Your chart reveals your Lagna identity, planetary strengths (Shadbala), active Vimshottari Mahadasha cycles, divisional charts (D1/D9), and potential strengths in career, relationships, and health."
    },
    {
      q: "Is this Birth Chart Calculator completely free to use?",
      a: "Yes, 100%! All 26 calculators on AstroPredict—including full Kundli generation, Vimshottari Dasha, Ashtakavarga, and Shadbala—are completely free with zero hidden paywalls."
    }
  ];

  const relatedCalculators = [
    {
      title: "Vimshottari Dasha Calculator",
      href: "/dasha-calculator",
      description: "Explore your 120-year Mahadasha and Antardasha planetary timeline.",
      categoryBadge: "Timeline"
    },
    {
      title: "Ashtakavarga Calculator",
      href: "/ashtakavarga-calculator",
      description: "View 337-bindu matrix scores across all 12 signs and 7 planets.",
      categoryBadge: "Matrix"
    },
    {
      title: "Moon Sign Calculator",
      href: "/moon-sign-calculator",
      description: "Discover your true Vedic Chandra Rashi and Nakshatra Pada.",
      categoryBadge: "Core"
    },
    {
      title: "Kundli Matching (36 Guna)",
      href: "/kundli-matching",
      description: "Serious 36-Point Ashtakoot Guna Milan for marital compatibility.",
      categoryBadge: "Compatibility"
    }
  ];

  return (
    <div className="space-y-8 max-w-4xl mx-auto py-4">
      {/* Header Banner */}
      <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 sm:p-8 shadow-xs space-y-3 text-center sm:text-left relative overflow-hidden">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/20 text-[var(--gold)] text-[10px] font-bold uppercase tracking-wider">
          <Sparkles className="w-4 h-4 text-[var(--gold)]" />
          <span>Vedic Ephemeris Birth Chart Generator</span>
        </div>

        <h1 className="text-2xl sm:text-4xl font-serif font-medium text-[var(--foreground)] flex items-center justify-center sm:justify-start gap-3">
          <Compass className="w-8 h-8 text-[var(--gold)] shrink-0" />
          <span>Birth Chart Calculator (Vedic Kundli)</span>
        </h1>

        <p className="text-xs sm:text-sm text-[var(--muted)] leading-relaxed max-w-2xl">
          Calculate your complete Vedic birth chart with Swiss Ephemeris astronomical precision. Generates true Lagna (Ascendant), 9 Graha longitudes, Nakshatra Padas, D1 &amp; D9 divisional charts, Ashtakavarga matrices, and 120-year Vimshottari Dasha timeline.
        </p>

        <div className="flex flex-wrap justify-center sm:justify-start gap-4 pt-2 text-xs text-[var(--muted)]">
          <div className="flex items-center gap-1.5 bg-[var(--background)] border border-[var(--line)] px-3 py-1 rounded-md">
            <ShieldCheck className="w-3.5 h-3.5 text-[var(--gold)]" />
            <span>Lahiri Ayanamsa</span>
          </div>
          <div className="flex items-center gap-1.5 bg-[var(--background)] border border-[var(--line)] px-3 py-1 rounded-md">
            <Orbit className="w-3.5 h-3.5 text-[var(--gold)]" />
            <span>Whole Sign Houses</span>
          </div>
          <div className="flex items-center gap-1.5 bg-[var(--background)] border border-[var(--line)] px-3 py-1 rounded-md">
            <Compass className="w-3.5 h-3.5 text-[var(--gold)]" />
            <span>True Node Rahu/Ketu</span>
          </div>
        </div>
      </div>

      {/* Main Interaction: Birth Details Form */}
      <section className="pt-2">
        <BirthDetailsForm />
      </section>

      {/* Reusable Supporting Content Component */}
      <CalculatorPageContent
        calculatorName="Birth Chart Calculator"
        introParagraph={introParagraph}
        methodologyTitle="How I Calculate Your Birth Chart"
        methodologyContent={methodologyContent}
        houseTableData={BIRTH_CHART_HOUSE_TABLE}
        subsections={subsections}
        faqs={faqs}
        relatedCalculators={relatedCalculators}
        showSignPeriodGrid={true}
      />
    </div>
  );
}
